/**
 * SectionCard —— 销售流程合并卡的【通用段卡】基础组件。
 * 卡头（标题 + 四态徽章 Pill + 折叠箭头，整行点击 onToggle）+ 折叠内容（200ms 过渡）。
 * 四态徽章：✅已完成 done / 🔵进行中 current / ⚪未开始 pending / 🟡待审批中 approval。
 * current=true：品牌蓝左边线 3px + 浅蓝底高亮（标识当前进行段）。
 * disabled=true：整卡 opacity 0.55、未到段卡头可点、展开显占位（不渲染 children、避免未到段挂载子卡发请求）。
 * 样式见 fulfillment.css（.flow-* 前缀、复用 incentive token、与 .inc-* 隔离）。
 */
import { ReactNode } from 'react';
import './fulfillment.css';

export type SectionBadge = 'done' | 'current' | 'pending' | 'approval';

/* 四态徽章配置：图标 emoji + 文案 + 对应 .flow-badge-- 修饰类 */
const BADGE_META: Record<SectionBadge, { ico: string; label: string; cls: string }> = {
  done:     { ico: '✅', label: '已完成',   cls: 'flow-badge--done' },
  current:  { ico: '🔵', label: '进行中',   cls: 'flow-badge--current' },
  pending:  { ico: '⚪', label: '未开始',   cls: 'flow-badge--pending' },
  approval: { ico: '🟡', label: '待审批中', cls: 'flow-badge--approval' },
};

export interface SectionCardProps {
  /** 锚点 id（合页 scrollIntoView 用，如 'flow-sec-5_deposit'） */
  id?: string;
  /** 段标题（可带图标节点） */
  title: ReactNode;
  /** 当前段状态徽章四态 */
  badge: SectionBadge;
  /** 是否当前进行段：true 加品牌蓝左边线 + 浅蓝底高亮 */
  current?: boolean;
  /** 是否未到段：true 整卡置灰、卡头不可点（父层不放操作按钮） */
  disabled?: boolean;
  /** 是否展开（受控） */
  expanded: boolean;
  /** 卡头点击回调（切换展开/收起） */
  onToggle: () => void;
  /** 可选：待审批说明行（黄底，如"待主管审批中"） */
  approvalNote?: string;
  /** 折叠内容 */
  children: ReactNode;
}

export function SectionCard({
  id, title, badge, current, disabled, expanded, onToggle, approvalNote, children,
}: SectionCardProps) {
  const meta = BADGE_META[badge];
  // 根容器修饰类：current 高亮 / disabled 置灰
  const rootCls = [
    'flow-sec',
    current ? 'flow-sec--current' : '',
    disabled ? 'flow-sec--disabled' : '',
  ].filter(Boolean).join(' ');

  return (
    <div id={id} className={rootCls}>
      {/* 卡头：整行点击切换；未到段卡头也可点击展开（展开只显占位） */}
      <div
        className="flow-sec-head"
        onClick={onToggle}
        role="button"
        aria-expanded={expanded}
      >
        <span className="flow-sec-title">{title}</span>
        {/* 四态徽章 Pill */}
        <span className={`flow-badge ${meta.cls}`}>
          <span className="flow-badge-ico">{meta.ico}</span>
          {meta.label}
        </span>
        {/* 折叠箭头：展开旋转 90° */}
        <span className={`flow-sec-arrow${expanded ? ' flow-sec-arrow--open' : ''}`}>▶</span>
      </div>

      {/* 待审批说明行（可选、黄底） */}
      {approvalNote && (
        <div className="flow-approval-note">
          <span>🟡</span>
          {approvalNote}
        </div>
      )}

      {/* 折叠内容：max-height + opacity 200ms 过渡，无布局跳动 */}
      <div className={`flow-collapse${expanded ? ' flow-collapse--open' : ''}`}>
        {/* 未到段（disabled）展开只显灰字占位、★绝不渲染 children★（避免发票段子卡挂载发请求） */}
        <div className="flow-sec-body">
          {disabled ? <div className="flow-locked-note">待前序步骤完成后开放</div> : children}
        </div>
      </div>
    </div>
  );
}
